import { stringify } from 'qs';

import { type Board, type Clip } from '@air/api/types';

import { sanitizeStringForUrl } from '~/utils/Url';

export const createBoardPathSegment = (id: string, title?: string): string =>
  `${title ? `${sanitizeStringForUrl(title)}-` : ''}${id}`;

export const createBoardRoute = (id: string, title?: string): string => `/b/${createBoardPathSegment(id, title)}`;

// @TODO(REV-821): Replace with actual collection route when implemented
export const createCollectionRoute = (): string => `/`;

export const createLibraryRoute = (id: string): string => `/l/${id}`;

export const createBoardClipRoute = ({
  clipId,
  boardTitle,
  boardId,
}: {
  clipId: Clip['id'];
  boardTitle: string;
  boardId: Board['id'];
}) => `${createBoardRoute(boardId, boardTitle)}/c/${clipId}`;

export const createBoardCanvasRoute = ({
  canvasId,
  boardTitle,
  boardId,
}: {
  canvasId: string;
  boardTitle?: string;
  boardId: Board['id'];
}) => `${createBoardRoute(boardId, boardTitle)}/canvas/${canvasId}`;

export const createSavedSearchRoute = (id: string) => `/saved-search/${id}`;

/** Legacy bookmark prefix; permanent redirect to {@link createSavedSearchRoute} in `next.config.js`. */
export const LEGACY_SAVED_FILTER_PATH_PREFIX = '/saved-filter/';

export const createShortUrlBoardRoute = (shortId: string, subBoardId: string) => `/a/${shortId}/b/${subBoardId}`;

export const createPublicShowcaseRoute = (shortId: string) => `/a/${shortId}`;

/**
 * The live share-link viewer's base path — every viewer segment (`/page/{slug}`, `/b/{boardId}`,
 * `/c/{clipId}`) appends to it. The owner-only preview surface has its own base
 * (`createShowcasePreviewRoute`) carrying the same segments.
 */
export const createShortUrlRoute = (shortId: string) => `/a/${shortId}`;

/** The owner-only showcase preview surface's base path — see `createShortUrlRoute`. */
export const createShowcasePreviewRoute = (showcaseId: string) => `/showcases/preview/${showcaseId}`;

/**
 * A collection share link's authored sub-page. The backend models sub-pages by `collectionId` only
 * — there is no slug field — so the slug is derived client-side from the sub-page's name by
 * `resolveShowcaseSubPageSlugs`. A raw collectionId is also accepted in the slug position when
 * resolving, so links minted before a rename still land on the right page.
 */
export const createShortUrlShowcaseSubPageRoute = (shortId: string, slug: string) =>
  `/a/${shortId}/page/${encodeURIComponent(slug)}`;

export const createShortUrlClipInBoardRoute = (shortId: string, subBoardId: string, clipId: Clip['id']) =>
  `/a/${shortId}/b/${subBoardId}/c/${clipId}`;

export const isSubscriptionCheckoutRoute = (pathname: string) => pathname.startsWith(Routes.workspaceAdmin.checkout);

export const isVerifyRoute = (pathname: string) => new URL(pathname, 'https://air.inc').pathname.startsWith('/verify/');

export const Routes = {
  root: '/',
  apiDocs: 'https://developer.air.inc',
  apiKeyHelp: 'https://help.air.inc/en/articles/7047153-api-key-access#h_87ab5c7911',
  brandfolderMigration: 'https://airinc.notion.site/Brandfolder-Air-bbda48cadae545d0977e7012758e095a',
  cantoMigration: 'https://airinc.notion.site/Canto-Air-cbf680bf6b6c41e6addc8f60184f52d5',
  home: '/home',
  helpCenter: 'https://help.air.inc',
  facialRecognition: 'https://help.air.inc/en/articles/11062794-explore-facial-recognition',
  changelog: 'https://airlabs.canny.io/changelog',
  createWorkspace: '/workspace/create',
  joinApprovedWorkspace: '/workspace/join',
  joinWorkspace: '/workspace/join/[shortId]',
  joinWorkspaceWithToken: '/workspace/join/[shortId]',
  redirectWorkspace: '/workspace/redirect',
  workspaceMembersLimit: '/workspace/users-limit',
  noWorkspacePermission: '/no-workspace-permission',
  verify: '/verify/[dummyCode]/[token]',
  verifyUser: '/verify-user',
  notFound: '/404',
  import: '/import/',
  canvases: '/canvases',
  canvas: '/canvases/[canvasId]',
  canvasNew: '/canvases/new',
  creativePerformance: '/creative-performance',
  showcases: '/showcases',
  skills: '/skills',
  automations: '/automations',
  imports: {
    googleDrive: '/import/drive',
    homeBox: '/import/box',
    brandfolder: '/import/brandfolder',
    canto: '/import/canto',
    dropbox: '/import/dropbox',
    sharePoint: '/import/sharepoint',
    frameio: '/import/frameio',
  },
  generalLibrary: createLibraryRoute('general'),
  desktop: {
    openDesktopFile: '/open-desktop-file',
  },
  marketing: {
    root: 'https://air.inc',
    adobePremiereProDownload: 'https://partners.adobe.com/exchangeprogram/creativecloud/exchange.details.107604.html',
    desktop: 'https://air.inc/air-flow',
    pricing: 'https://air.inc/pricing',
    privacy: 'https://air.inc/privacy',
    terms: 'https://air.inc/terms',
  },
  auth: {
    login: '/login',
    logout: '/logout',
    forgot: '/forgot',
    samlSSOLogin: '/saml-sso-login',
    /** Requires "email" query string */
    resetPassword: '/reset',
    oauth: '/oauth',
    oauthConsent: '/oauth/consent',
    signUp: '/sign-up',
  },
  popupConfirmation: '/popup-confirmation',
  onboarding: {
    createWorkspace: '/onboarding/workspace-name',
    approvedDomains: '/onboarding/join-approved-workspaces',
  },
  account: {
    apiAccess: '/account/api',
    myAccount: '/account/my-account',
    changePassword: '/account/change-password',
    confirmEmail: '/account/confirm-email',
    notificationSettings: '/account/notifications',
    integrations: '/account/integrations',
  },
  media: {
    favoriteItems: '/favorite-items',
    all: '/all',
    myUploads: '/my-uploads',
    recentlyAdded: '/recently-added',
    recentlyDeleted: '/recently-deleted',
    recentlyModified: '/recently-modified',
    unusedItems: '/unused-items',
    untaggedItems: '/untagged-items',
  },
  workspaceAdmin: {
    addOns: '/workspace/add-ons',
    checkout: '/workspace/subscription/checkout',
    customFields: '/workspace/custom-fields',
    tags: '/workspace/tags',
    cdnLinks: '/workspace/cdn-links',
    shareLinkDefaults: '/workspace/share-link-defaults',
    edit: '/workspace/edit',
    invoices: '/workspace/invoices',
    subscription: '/workspace/subscription',
    security: '/workspace/security',
    auditLogs: '/workspace/audit-logs',
    people: '/workspace/people',
    adInsightsAccounts: '/workspace/ad-insights-accounts',
    imports: '/workspace/imports',
  },
  workspaceSettings: {
    root: '/workspace',
    edit: '/workspace/edit',
    members: '/workspace/users',
  },
  people: '/people',
  selectSSOProviderToLoginTo: '/select-sso-provider-to-login-to',
  portal: {
    development: 'https://qa.portal.air.inc',
    preview: 'https://qa.portal.air.inc',
    production: 'https://portal.air.inc',
  },
  creditsHelp: 'https://help.air.inc/en/articles/14178814-explore-credits',
};

export const workspaceRoutes = [Object.values(Routes.workspaceSettings), Object.values(Routes.workspaceAdmin)].flat();
export const accountSettingsRoutes = Object.values(Routes.account);

export const createAllContentAssetRoute = (id: string) => `${Routes.media.all}/c/${id}`;
export const createShowcaseRoute = (showcaseId: string) => `${Routes.showcases}/${showcaseId}`;

export const getJoinWorkspaceURL = (shortId: string, email: string) =>
  Routes.joinWorkspace.replace('[shortId]', shortId).concat(`?${stringify({ email })}`);

export const getDesktopAppLink = (pathname: string, search = '') => {
  return `airapp://.${pathname}/${search}`;
  //               ^           ^
  // Both the dot and trailing slash in this url are significant. The
  // desktop app will receive this url and extract pathname using
  // `new URL('airapp://./...')` and the dot ensures that the url is valid
  // since it the pathname will include a backslash at the beginning.
};

export const AirFlowPageLink = 'https://air.inc/air-flow';
